import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiX } from 'react-icons/fi';
import { track } from '@/lib/analytics';
import { apiJson, ApiError } from '@/lib/api';
import { Button, Input } from '@/components';
import type {
  PendingProfessionalConnection,
  PendingProfessionalConnectionListResponse,
  ProfessionalInviteListResponse,
  ProfessionalInviteResponse,
} from '@/lib/types';

function inviteStatusLabel(inv: ProfessionalInviteResponse): string {
  if (inv.status === 'accepted') return 'Connected';
  if (inv.status === 'pending') return 'Pending';
  if (inv.status === 'pending_coparent_share') return 'Awaiting your approval';
  return inv.status;
}

type ShareFlags = {
  share_chats: boolean;
  share_expenses: boolean;
  share_custody_calendar: boolean;
  share_journal: boolean;
};

function ShareToggles({
  flags,
  onChange,
  disabled,
}: {
  flags: ShareFlags;
  onChange: (flags: ShareFlags) => void;
  disabled?: boolean;
}) {
  const items: { key: keyof ShareFlags; label: string }[] = [
    { key: 'share_chats', label: 'Messages' },
    { key: 'share_expenses', label: 'Expenses' },
    { key: 'share_custody_calendar', label: 'Custody calendar' },
    { key: 'share_journal', label: 'Journal' },
  ];

  return (
    <div className='space-y-2'>
      {items.map(({ key, label }) => (
        <label key={key} className='flex items-center justify-between py-1'>
          <span className='text-sm text-gray-700'>{label}</span>
          <input
            type='checkbox'
            checked={flags[key]}
            disabled={disabled}
            onChange={(e) => onChange({ ...flags, [key]: e.target.checked })}
            className='w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary'
          />
        </label>
      ))}
    </div>
  );
}

export function ProfessionalInvitesCard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [manageInvite, setManageInvite] = useState<ProfessionalInviteResponse | null>(null);
  const [pendingConnection, setPendingConnection] =
    useState<PendingProfessionalConnection | null>(null);
  const [email, setEmail] = useState('');
  const [shareFlags, setShareFlags] = useState<ShareFlags>({
    share_chats: true,
    share_expenses: true,
    share_custody_calendar: true,
    share_journal: true,
  });
  const [error, setError] = useState('');

  const invitesQuery = useQuery({
    queryKey: ['professional-invites'],
    queryFn: async () => {
      try {
        return await apiJson<ProfessionalInviteListResponse>(
          getToken,
          '/v1/professional-invites'
        );
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return { invites: [] };
        }
        throw err;
      }
    },
  });

  const pendingQuery = useQuery({
    queryKey: ['professional-connections-pending'],
    queryFn: async () => {
      try {
        return await apiJson<PendingProfessionalConnectionListResponse>(
          getToken,
          '/v1/professional-connections/pending'
        );
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return { connections: [] };
        }
        throw err;
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiJson<ProfessionalInviteResponse>(getToken, '/v1/professional-invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ professional_email: email.trim(), ...shareFlags }),
      }),
    onSuccess: () => {
      track('Professional Invite Sent');
      void queryClient.invalidateQueries({ queryKey: ['professional-invites'] });
      setInviteModalOpen(false);
      setEmail('');
      setError('');
    },
    onError: (err: Error) => {
      setError(err.message || "Couldn't send invite.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: number } & ShareFlags) =>
      apiJson<ProfessionalInviteResponse>(getToken, `/v1/professional-invites/${data.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          share_chats: data.share_chats,
          share_expenses: data.share_expenses,
          share_custody_calendar: data.share_custody_calendar,
          share_journal: data.share_journal,
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['professional-invites'] });
      setManageInvite(null);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson(getToken, `/v1/professional-invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      track('Professional Invite Revoked');
      void queryClient.invalidateQueries({ queryKey: ['professional-invites'] });
      setManageInvite(null);
    },
  });

  const acceptPendingMutation = useMutation({
    mutationFn: (data: { invite_id: number } & ShareFlags) =>
      apiJson(getToken, `/v1/professional-connections/${data.invite_id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          share_chats: data.share_chats,
          share_expenses: data.share_expenses,
          share_custody_calendar: data.share_custody_calendar,
          share_journal: data.share_journal,
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['professional-invites'] });
      void queryClient.invalidateQueries({ queryKey: ['professional-connections-pending'] });
      setPendingConnection(null);
    },
  });

  const invites = invitesQuery.data?.invites ?? [];
  const pending = pendingQuery.data?.connections ?? [];

  const openManage = (inv: ProfessionalInviteResponse) => {
    setManageInvite(inv);
    setShareFlags({
      share_chats: inv.share_chats,
      share_expenses: inv.share_expenses,
      share_custody_calendar: inv.share_custody_calendar,
      share_journal: inv.share_journal,
    });
  };

  const openPending = (conn: PendingProfessionalConnection) => {
    setPendingConnection(conn);
    setShareFlags({
      share_chats: conn.share_chats,
      share_expenses: conn.share_expenses,
      share_custody_calendar: conn.share_custody_calendar,
      share_journal: conn.share_journal,
    });
  };

  return (
    <div>
      <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
        Professional access
      </h2>
      <p className='text-sm text-gray-500 mb-4'>
        Invite your lawyer, mediator, or coach with read-only access to selected Harbor records.
      </p>

      {pending.length > 0 && (
        <div className='mb-4 space-y-2'>
          {pending.map((conn) => (
            <button
              key={conn.invite_id}
              type='button'
              onClick={() => openPending(conn)}
              className='w-full text-left p-3 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors'
            >
              <p className='font-semibold text-gray-900 text-sm'>
                {conn.professional_name ?? conn.professional_email}
              </p>
              <p className='text-xs text-amber-800 mt-0.5'>Awaiting your sharing approval</p>
            </button>
          ))}
        </div>
      )}

      {invites.length > 0 && (
        <div className='space-y-2 mb-4'>
          {invites.map((inv) => (
            <button
              key={inv.id}
              type='button'
              onClick={() => openManage(inv)}
              className='w-full flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left'
            >
              <div className='min-w-0'>
                <p className='font-semibold text-gray-900 text-sm truncate'>
                  {inv.professional_name ?? inv.professional_email}
                </p>
                <p className='text-xs text-gray-500'>{inviteStatusLabel(inv)}</p>
              </div>
              <span className='text-xs text-primary font-semibold shrink-0 ml-2'>Manage</span>
            </button>
          ))}
        </div>
      )}

      <Button
        fullWidth
        variant='secondary'
        onClick={() => {
          track('Professional Invite Modal Opened');
          setInviteModalOpen(true);
          setShareFlags({
            share_chats: true,
            share_expenses: true,
            share_custody_calendar: true,
            share_journal: true,
          });
          setError('');
        }}
      >
        Invite a professional
      </Button>

      {inviteModalOpen && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-xl font-bold text-gray-900'>Invite a professional</h3>
              <button type='button' onClick={() => setInviteModalOpen(false)} className='p-2 hover:bg-gray-100 rounded-full'>
                <FiX className='w-5 h-5' />
              </button>
            </div>
            <Input
              label='Professional email'
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='coach@example.com'
            />
            <p className='text-sm font-semibold text-gray-700 mt-4 mb-2'>What to share</p>
            <ShareToggles flags={shareFlags} onChange={setShareFlags} />
            {error && <p className='text-sm text-red-500 mt-3'>{error}</p>}
            <div className='flex gap-3 mt-6'>
              <Button variant='secondary' fullWidth onClick={() => setInviteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={() => createMutation.mutate()}
                loading={createMutation.isPending}
                disabled={!email.trim() || createMutation.isPending}
              >
                Send invite
              </Button>
            </div>
          </div>
        </div>
      )}

      {manageInvite && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-xl font-bold text-gray-900'>Manage access</h3>
              <button type='button' onClick={() => setManageInvite(null)} className='p-2 hover:bg-gray-100 rounded-full'>
                <FiX className='w-5 h-5' />
              </button>
            </div>
            <p className='text-sm text-gray-500 mb-4'>{manageInvite.professional_email}</p>
            <ShareToggles flags={shareFlags} onChange={setShareFlags} />
            <div className='flex flex-col gap-2 mt-6'>
              <Button
                fullWidth
                onClick={() =>
                  updateMutation.mutate({ id: manageInvite.id, ...shareFlags })
                }
                loading={updateMutation.isPending}
              >
                Save changes
              </Button>
              <Button
                variant='ghost'
                fullWidth
                className='text-red-600 border border-red-200'
                onClick={() => {
                  if (window.confirm("Remove this professional's access?")) {
                    revokeMutation.mutate(manageInvite.id);
                  }
                }}
                loading={revokeMutation.isPending}
              >
                Revoke access
              </Button>
            </div>
          </div>
        </div>
      )}

      {pendingConnection && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-xl font-bold text-gray-900'>Approve sharing</h3>
              <button type='button' onClick={() => setPendingConnection(null)} className='p-2 hover:bg-gray-100 rounded-full'>
                <FiX className='w-5 h-5' />
              </button>
            </div>
            <p className='text-sm text-gray-500 mb-4'>
              {pendingConnection.professional_name ?? pendingConnection.professional_email} requested
              access. Choose what to share.
            </p>
            <ShareToggles flags={shareFlags} onChange={setShareFlags} />
            <div className='flex gap-3 mt-6'>
              <Button variant='secondary' fullWidth onClick={() => setPendingConnection(null)}>
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={() =>
                  acceptPendingMutation.mutate({
                    invite_id: pendingConnection.invite_id,
                    ...shareFlags,
                  })
                }
                loading={acceptPendingMutation.isPending}
              >
                Enable sharing
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
